"use client";

import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useSelector } from "react-redux";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, UserPlus } from "lucide-react";
import type { iUserAccessV2 } from "@sdk/access/access.models";
import type { InviteUsersResult } from "@/@sdk/user-management/user-management.models";
import { useSDK } from "@/@sdk/sdk.hooks";
import { selectAllBrands } from "@/store/modules/brands/brands.selectors";
import {
  UserAccessEditor,
  createInviteDefaultAccessV2,
} from "./user-access-editor";
import {
  MAX_INVITE_EMAILS,
  parseInviteEmailsInput,
} from "./invite-user.helpers";

const inviteUserSchema = z.object({
  emails: z
    .string()
    .min(1, "Enter at least one email address")
    .superRefine((value, ctx) => {
      const emails = parseInviteEmailsInput(value);

      if (emails.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Enter at least one valid email address",
        });
        return;
      }

      if (emails.length > MAX_INVITE_EMAILS) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `You can invite up to ${MAX_INVITE_EMAILS} users at a time`,
        });
        return;
      }

      const invalidEmail = emails.find(
        (email) => !z.string().email().safeParse(email).success
      );
      if (invalidEmail) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Invalid email address: ${invalidEmail}`,
        });
      }
    }),
});

type InviteUserFormValues = z.infer<typeof inviteUserSchema>;

export interface InviteUserPayload {
  emails: string[];
  access: iUserAccessV2;
}

interface InviteUserProps {
  onInvite: (
    payload: InviteUserPayload
  ) => void | Promise<void | InviteUsersResult | undefined>;
  onInvited?: (result: InviteUsersResult) => void;
  invitesRefreshKey?: number;
}

export function InviteUser({
  onInvite,
  onInvited,
  invitesRefreshKey = 0,
}: InviteUserProps) {
  const brands = useSelector(selectAllBrands);
  const activeBrandIds = useMemo(
    () =>
      brands
        .filter((brand) => brand.id && brand.isDisabled !== true)
        .map((brand) => brand.id!),
    [brands]
  );
  const defaultAccess = useMemo(
    () => createInviteDefaultAccessV2(activeBrandIds),
    [activeBrandIds]
  );

  const {
    data: pendingInvites,
    isLoading: isLoadingPendingInvites,
    reload: reloadPendingInvites,
  } = useSDK((SDK) => SDK.getAllActiveInvitation(), [], false, []);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showFormExpanded, setShowFormExpanded] = useState(false);
  const [access, setAccess] = useState<iUserAccessV2>(() => defaultAccess);

  const form = useForm<InviteUserFormValues>({
    resolver: zodResolver(inviteUserSchema),
    defaultValues: {
      emails: "",
    },
  });

  useEffect(() => {
    void reloadPendingInvites();
  }, [invitesRefreshKey, reloadPendingInvites]);

  const hasPendingInvites = pendingInvites.length > 0;
  const isFormVisible =
    !isLoadingPendingInvites && (!hasPendingInvites || showFormExpanded);

  const onSubmit = async (data: InviteUserFormValues) => {
    const emails = parseInviteEmailsInput(data.emails);
    setIsSubmitting(true);
    try {
      const result = await onInvite({ emails, access });
      form.reset();
      setAccess(createInviteDefaultAccessV2(activeBrandIds));
      await reloadPendingInvites();
      if (result?.invitations.length) {
        setShowFormExpanded(false);
      }
      if (result) {
        onInvited?.(result);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoadingPendingInvites) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading invitations…
      </div>
    );
  }

  if (!isFormVisible) {
    return (
      <div className="flex justify-center">
        <Button
          type="button"
          variant="outline"
          onClick={() => setShowFormExpanded(true)}
        >
          <UserPlus className="mr-2 h-4 w-4" />
          Send more invitations
        </Button>
      </div>
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="emails"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email addresses</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="alice@example.com, bob@example.com"
                  className="min-h-[96px]"
                  {...field}
                />
              </FormControl>
              <FormDescription>
                Enter up to {MAX_INVITE_EMAILS} email addresses, separated by
                commas, semicolons, or new lines.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="space-y-2">
          <FormLabel>Access</FormLabel>
          <UserAccessEditor value={access} onChange={setAccess} />
        </div>

        <div className="flex justify-end">
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Send invitations
          </Button>
        </div>
      </form>
    </Form>
  );
}
